import * as React from "react";
import { App } from "./app"
import { ExtDataGW } from "./ExtDataGW"
import { ArticleOrderGW } from "./ArticleOrderGW";
import { MakeNational } from "./BDKalkCore"
import { PADebug } from "./PADebug"

// Parameter set for ArticleGw
export class ArticleStructGW {
    public m_articleID: string // A unique product key used within BDKalk only, general for any ware/service
    public m_eanNo: string // A string of numbers relevant within binary optical code labeling
    public m_identifier: string // The identification key used by the producer/distributor of the ware/service
    public m_identifierOld: string // The identification key used by the producer/distributor of the ware/service
    public m_groupID: string // A string of numbers telling within what group of articles this article should be placed
    public m_title: string // A general title, better kept short and informative due to its various uses
    public m_description: string // A short text (32 bytes) that describes the item
    public m_description2: string // Another short text (32 bytes) adding more detail to the description
    public m_descriptionUrl: string // A link to a text file describing the product in more detail
    public m_websiteLink: string // A link to a webpage where the product is described by its producer
    public m_imageUrl: string // A link to an image file
    private m_amount: number // To be used wherever this is relevant 
    public m_packageAmount: string // the amount within each package from the producer/distributor
    public m_unit: string // The unit in choise for describing and measuring the amount of this ware
    public m_packageUnit: string // A word describing a package of this article
    public m_pricePurchase: number // The article's purchasing prize
    public m_priceBase: number // The articles base prize
    public m_priceRecommended: number // A recommended prize for the ware
    public m_prizeFactory: number // The article's factory prize
    public m_discountFactor: number // Holds the discount percentage as a number between 0.0 and 1.0
    public m_currency: string // The currency in choise for prizing of the ware/service
    public m_nobb: string // Nobb number
    public m_nobbOwner: string // Nobb owner number
    public m_nobbModule: string // Nobb module number
    public m_prizeType: string // Type of prize
    public m_itemType: string // A code describing the size
    public m_supplier: string // The name of the supplier
    public m_priceBook: number // The ID for the pricebook where to find this article
}

/* << ArticleGW >>
   ArticleGW stores the related data to any type of selectable ware or service,
   as a generalised piece of data that can be selected and localised within various
   views, files or databases, and it is not any graphical component on its own.
   ArticleGW does not however contain all available data for an article but a
   shorter version of it, with reasonable enough size to be held widely in memory,
   while the more bulky data available for each article will be requested from
   the back end on demand, such as when opening the extended info for a selected item.
   These additional data are stored, for as long as they are needed, within an ExtDataGW
   instance which can (if it is loaded) be accessed from the GetIfHasExtData() method,
   which may return null if the extended data for this article is not presently loaded. */

export class ArticleGW {
    constructor(
        articleID: string,
        eanNo: string,
        identifier: string,
        identifierOld: string,
        groupID: string,
        description: string,
        description2: string,
        descriptionUrl: string,
        websiteLink: string,
        imageUrl: string,
        packageAmount: number,
        unit: string,
        pricePurchase: number,
        priceBase: number,
        priceRecommended: number,
        priceFactory: number,
        packageUnit: string,
        currency: string,
        nobb: string,
        nobbOwner: string,
        nobbModule: string,
        prizeType: string,
        itemType: string,
        supplier: string,
        amount: number,
        discountFactor: number,
        priceBook:number
    ) {
        this.m_articleID = articleID
        this.m_eanNo = eanNo
        this.m_identifier = identifier
        this.m_identifierOld = identifierOld
        this.m_groupID = groupID
        this.m_description = description
        this.m_description2 = description2
        this.m_descriptionUrl = descriptionUrl
        this.m_websiteLink = websiteLink
        this.m_imageUrl = imageUrl
        this.m_packageAmount = packageAmount
        this.m_unit = unit
        this.m_pricePurchase = pricePurchase
        this.m_priceBase = priceBase
        this.m_priceRecommended = priceRecommended
        this.m_priceFacory = priceFactory
        this.m_packageUnit = packageUnit
        this.m_currency = currency
        this.m_nobb = nobb
        this.m_nobbOwner = nobbOwner
        this.m_nobbModule = nobbModule
        this.m_prizeType = prizeType
        this.m_itemType = itemType
        this.m_supplier = supplier
        this.m_amount = amount
        this.m_discountFactor = discountFactor
        this.m_priceBook = priceBook 
        this.m_extData = null
        this.m_cathegoricWords = null
    }

    // Returns a set of extended data for this article if the article holds a ExtDataGW object, which will
    // happen if the article was selected, ext-data was loaded for it and the user has added an amount into the selected article
    public GetIfHasExtData() {
        if (this.m_extData != null) {
            return this.m_extData
        }
        else {
            return null
        }
    }

    /* This adds the on top data which can, but won't always be referrenced from an article, as a rule of thumb, articles does not have
     * this added data if they don't occur in the quick report of selected items, once a selected item is removed from the quick report
     * section, its corresponding extended data object is removed and won't remain in memory. */
    public SetExtData(newReference: ExtDataGW | null) {
        this.m_extData = newReference
    }

    /* This method is a handler for the onloaded event when the server returns requested ext data for an article. In some cases, the extended
     * data is not supposed to be attatched to the article, when it is just viewed but need no permanent instance of the data since no amount
     * was set, in those cases the instance is instead temporarily held with the core.m_extDataForSelectedItem reference, and will be replaced
     * once a new article is selected for review. But if this handler is being used, an ext data object will be created and attatched to this article
     * and after that the views must be updated so that the previous render that showed "Loading.." (or similar) will be properly replaced.     */
    public ReciveExtDataMaterial(data: string) {
         
    }

    public RenderOrders(stage: number, flags:number, shouldConvert: boolean, fpakSum: { sum: number }) {
        if (this.m_extData != null) {
            if (this.m_extData.GetIsDefault() == true) {
                const order = new ArticleOrderGW()
                order.SetRenderingMode(stage, flags)
                order.SetAmount(this.GetAmount())
                order.SetSupplierName(this.GetSupplier())
                order.SetSupplierId(this.GetSupplyIdentifier())
                order.SetUnit(this.GetUnit())
                order.SetUnitId(this.GetUnit())
                order.SetNobb(this.m_extData.GetNumberFromNobbString(this.GetNobb()))
                order.SetArticle(this)
                order.SetPackageUnits(1.0)
                order.SetPriceBook(this.GetPriceBook())
                order.SetOrderingId(this.GetIdentifier())

                if (shouldConvert == true) {
                    fpakSum.sum += order.GetSum(stage, flags & 3)
                }

                return <div>{order.Render()}</div>
            }

            if (shouldConvert == true) {
                var order = this.m_extData.GetFpakOrder(stage, flags | 8, this, true)
                if (order) {
                    fpakSum.sum += order.GetSum(stage, flags & 3)

                    return <div>{order.Render()}</div>
                }
            }
            else {
                var order = this.m_extData.GetOrder(stage, flags, this)
                if (order) {
                    return <div>{order.Render()}</div>
                }
            }
        }
        else {
            return <div></div>
        }
        
        return <div>Error: ArticleGW.RenderOrders - Order from extData was undefined.</div>
    }

    // Freeing the extended data reference from this article
    public ClearExtData() {
        this.m_extData = null
    }

    // Returning the for BDKalk specific ID of the article
    public GetArticleID() {
        return this.m_articleID;
    }

    // Returning the purchasing prize for the article
    public GetType() {
        return this.m_itemType;
    }

    // Returning the prize type for the article
    public GetPrizeType() {
        return this.m_prizeType;
    }

    // Returning the base prize for the article
    public GetBasePrize() {
        return this.m_priceBase;
    }

    // Returning the purchasing prize for the article
    public GetPurchasePrize() {
        return this.m_pricePurchase;
    }

    // Returning the reccommended prize for the article
    public GetRecommendedPrize() {
        return this.m_priceRecommended;
    }
    
    // Returning the identification key used by the producer/distributor of the ware/service
    public GetIdentifier() {
        return this.m_identifier;
    }
    
    // Returning the nobb structure owner ID
    public GetNobbOwner() {
        return this.m_nobbOwner;
    }
    
    // Returning the nobb structure Module
    public GetNobbModule() {
        return this.m_nobbModule;
    }

    // Returning the title of the article
    public GetDescription() {
        return this.m_description;
    }
    
    // Returning the article's description text
    public GetDescription2() {
        return this.m_description2;
    }

    // Returning a link to a text file describing the product in more detail
    public GetDescriptionUrl() {
        return this.m_descriptionUrl;
    }

    // Returning a link to a webpage where the product is described by its producer
    public GetWebsiteUrl() {
        return this.m_websiteLink;
    }

    // Returns an url to an image file for the product
    public GetImageUrl() {
        return this.m_imageUrl;
    }

    // returns the name of the distributor as a string
    public GetDistributor() {
        return this.m_supplier;
    }

    // Returns the amount of the product within each package from the producer/distributor
    public GetAmountPerPackage() {
        return this.m_packageAmount;
    }

    // returns the amount of this article that has been chosen
    public GetAmount() {
        if (this.m_extData != null) {
            if (this.m_extData.GetIsDefault() == false) {
                var amount = this.m_extData.GetAmount()

                if (amount) {
                    this.m_amount = amount
                }
            }
        }

        return this.m_amount;
    }

    // returns the amount of this article that has been chosen
    public GetAmountHidden() {
        if (this.m_extData != null) {
            if (this.m_extData.GetIsDefault() == false) {
                return this.m_extData.GetAmountHidden()
            }
        }

        return false;
    }
    
    // Lets extended data attatched to this article inherit the pre given local amount of the article
    public ApplyLocalAmount() {
        if (this.m_extData != null) {
            if (this.m_extData.GetIsDefault() == false) {
                this.m_extData.SetAmount(this.m_amount)
            }
        }
    }

    // Sets the amount chosen for this article
    public SetAmount(newAmount: number) {
        this.m_amount = newAmount;

        if (this.m_amount <= 0) {
            this.m_amount = 0
        }

        if (this.m_extData != null) {
            if (this.m_extData.GetIsDefault() == false) {
                this.m_extData.SetAmount(newAmount)
            }
        }
    }

    // Returns the unit in choise for describing and measuring the amount of this ware
    public GetUnit() {
        return this.m_unit;
    }

    // Returns the unit in choise for describing and measuring the amount of this ware - locally
    public GetLocalUnit() {
        return this.m_unit;
    }
    
    public GetFactoryPrize() {
        return this.m_pricePurchase
    }

    // Returns the prize for each unit of the ware/service
    public GetUnitPrice(radioPos: number, flags: number) {

        var mva = 1.0
        var fpakFactor = 1.0

        if ((flags & 4) == 4) {
            mva = 1.25
        }

        if ((flags & 8) == 8) {
            var ext = this.GetIfHasExtData()
            if (ext) {
                if (ext.GetIsDefault() == false) {
                    var prop = ext.GetFpakProportion(this)
                    fpakFactor = (prop != undefined)?prop:0 // will never be the given 0 since this isn't the default extData for articles missing it.
                }
            }
            else {
                alert("Error in ArticleGW - Unit price was requested for fpak unit but with no ext info provided.")
            }
        }

        if (radioPos == 0) {
            return 0
        }

        if (radioPos == 1) {
            return this.GetBasePrize() * mva * fpakFactor
        }

        if (radioPos == 2) {
            return this.GetRecommendedPrize() * mva * fpakFactor
        }

        if (radioPos == 3) {
            return this.GetFactoryPrize() * mva * fpakFactor
        }

        alert("Error in ArticleGW.GetUnitPrice - unknown Radio Position!")
        return -111; // error
    }

    // Returns the currency for the given prize of this article
    public GetCurrency() {
        return this.m_currency;
    }

    // Returns the ean number which is practically used within stripe codes upon wares
    public GetEanNo(currentSelectedArticleData: ExtDataGW | null) {

        var addition = ""

        if (this.GetIfHasExtData() != null) {
            var extData = this.GetIfHasExtData()
            if (extData) {
                if (extData.GetIsDefault() == false) {
                    var se = extData.GetSpecificEan()
                    if (se) {
                        addition = se
                    }
                }
            }
        }
        else {
            if (currentSelectedArticleData != null) {
                var se = currentSelectedArticleData.GetSpecificEan()
                if (se) {
                    addition = se
                }
            }
        }

        if ((this.m_eanNo == addition)||(addition == "")) {
            return this.m_eanNo
        }
        else {
            return addition + "(custom)"
        }
    }

    public GetLocalEanNo() {
        return this.m_eanNo
    }

    // Returns an identifier (string) for this article, presently being in use by the supplier
    public GetSupplyIdentifier() {
        return this.m_identifier
    }

    // Returns an identifier previously used by the supplier (it will be displayed as 'old ID' if it exists)
    public GetSupplyIdentifierOld() {
        return this.m_identifierOld
    }

    // Returns the nobb code of the article
    public GetNobb() {
        return this.m_nobb
    }
    
    // Returns the nobb code of the article
    public GetPackageUnit() {
        return this.m_packageUnit
    }

    // Returns the calculated amount of price units for the fpak unit
    public GetFpakRatio() {
        if (this.m_extData != null) {
            var ratio = this.m_extData.GetFpakProportion(this)

            if (ratio) {
                return ratio
            }
        }

        PADebug.Assert(false, "ArticleGW.GetFpakRatio - Could not determine FPAK ratio for article which were either missing ext data or held incomplete data.")
        return 1.0 // Proper fpak proportion was not found for this article
    }

    // Returns the calculated prize for the chosen amount(s) of this article
    public GetSum(radioPos: number, flags: number) {
        if (this.m_extData != null) {
            if ((this.m_extData.GetIsDefault() == false) && ((flags & 8) != 0)) {
                var fpProp = this.m_extData.GetFpakProportion(this)

                if (fpProp && fpProp != 0) {
                    var fpakAmount = this.GetAmount() / fpProp 
                    var excess = fpakAmount % 1.0
                    var fulls = fpakAmount - excess
                    if (excess != 0) {
                        fulls++
                    }

                    var fpakUnitPrice = this.GetUnitPrice(radioPos, flags & (247)) * fpProp
                    
                    return fulls * fpakUnitPrice
                }
                else {
                    return this.GetAmount() * this.GetUnitPrice(radioPos, flags)
                }
            }
            else {
                return this.GetAmount() * this.GetUnitPrice(radioPos, flags)
            }
        }
        else {
            return this.GetAmount() * this.GetUnitPrice(radioPos, flags)
        }
    }

    // GetSumString returns the total prize for this one article (with its combined choises), and trims the prize to always have 2 decimals 
    public GetSumString(radioPos: number, flags: number) {
        var result = this.GetSum(radioPos, flags).toString()
        var decimalsShown = 0
        var passedComma = false

        // Counting the decimals of the total sum string
        for (var i = 0; i < result.length; i++) {
            if (passedComma == true) {
                decimalsShown++
            }

            // Ensuring there is no more than two decimals shown
            if (decimalsShown > 2) {
                result = result.slice(0, i)
                break
            }

            if (result[i] == '.') {
                passedComma = true
            }
        }

        // Ensuring there is allways two decimals shown on the total sum 
        if (decimalsShown == 0) {
            result += ".00"
        }

        if (decimalsShown == 1) {
            result += "0"
        }

        return MakeNational(result)
    }
    
    // Access for the discount factor
    public GetDiscountFactor() {
        return this.m_discountFactor
    }

    // Access for the supplier name
    public GetSupplier() {
        return this.m_supplier
    }

    // GetUnitPrizeString returns the unit prize for this one article, and trims the prize to always have 2 decimals
    public GetUnitPriceString(radioPos: number, flags: number, roundToFpak: boolean, extD: ExtDataGW | null) {
        var result = this.GetUnitPrice(radioPos, flags).toString()

        if (extD != null) {
            if (extD.GetIsDefault() == false) {
                var camt = extD.GetUnitCalculatedAmount()
                if (camt) {
                    result = (camt * this.GetUnitPrice(radioPos, flags)).toString()
                }
            }
        }

        var decimalsShown = 0
        var passedComma = false

        // Counting the decimals of the total sum string
        for (var i = 0; i < result.length; i++) {
            if (passedComma == true) {
                decimalsShown++
            }

            // Ensuring there is no more than two decimals shown
            if (decimalsShown > 2) {
                result = result.slice(0, i)
                break
            }

            if (result[i] == '.') {
                passedComma = true
            }
        }

        // Ensuring there is allways two decimals shown on the total sum 
        if (decimalsShown == 0) {
            result += ".00"
        }

        if (decimalsShown == 1) {
            result += "0"
        }

        return MakeNational(result)
    }
    
    // Adds an amount to the existing amount for this article
    public AddAmount(amount: number) {
        var sum = this.GetAmount() + amount
        if (sum <= 0) {
            sum = 0
        }

        this.SetAmount(sum)
    }

    // Returns the identifier of this article
    GetGroupID() {
        return this.m_groupID
    }

    // Access : Pricebook
    public GetPriceBook() {
        return this.m_priceBook
    }

    // Access : Get CathegoricWords
    public GetCathegoricWords() {
        return this.m_cathegoricWords
    }

    // Access : Set CathegoricWords
    public SetCathegoricWords(words:string) {
        this.m_cathegoricWords = words
    }

    // Access : Add CathegoricWords
    public AddCathegoricWord(word: string) {
        if (this.m_cathegoricWords) {
            this.m_cathegoricWords += word
        }
        else {
            this.m_cathegoricWords = word
        }
    }

    // Generates a string which is unique for this article and which will be compared to tell if this article is selected 
    public GetSelectionLabel() {
        var label = "PB:" + this.m_priceBook.toString() + "_ID:" + this.GetIdentifier()
        return label
    }

    // This is what will be displayed as the unit price of an article, but which may be a different package type
    // if the user has selected one different than the default package type, and will then calculate that price.
    public GetPackagePrice(radio : number, flags:number) {
        return this.GetUnitPrice(radio, flags)
    }
    
    // Returns a copy of this article
    public GetCopy() {
        var copy = new ArticleGW(
            this.m_articleID,
            this.m_eanNo,
            this.m_identifier,
            this.m_identifierOld,
            this.m_groupID,
            this.m_description,
            this.m_description2,
            this.m_descriptionUrl,
            this.m_websiteLink,
            this.m_imageUrl,
            this.m_packageAmount,
            this.m_unit,
            this.m_pricePurchase,
            this.m_priceBase,
            this.m_priceRecommended,
            this.m_priceFacory,
            this.m_packageUnit,
            this.m_currency,
            this.m_nobb,
            this.m_nobbOwner,
            this.m_nobbModule,
            this.m_prizeType,
            this.m_itemType,
            this.m_supplier,
            this.m_amount,
            this.m_discountFactor,
            this.m_priceBook)
        copy.m_extData = this.m_extData
        return copy
    }

    // Member variables for ArticleGW:
    private m_articleID: string // A unique product key used within BDKalk only, general for any ware/service
    private m_eanNo: string // A string of numbers relevant within binary optical code labeling
    private m_identifier: string // The identification key used by the producer/distributor of the ware/service
    private m_identifierOld: string // The identification key used by the producer/distributor of the ware/service
    private m_groupID: string // A string of numbers telling within what group of articles this article should
    private m_description: string // A short text that describes the item in about one or two sentences at most
    private m_description2: string // A short text that describes the item in about one or two sentences at most
    private m_descriptionUrl: string // A link to a text file describing the product in more detail
    private m_websiteLink: string // A link to a webpage where the product is described by its producer
    private m_imageUrl: string // A link to an image file
    private m_packageAmount: number // the amount within each package from the producer/distributor
    private m_unit: string // The unit in choise for describing and measuring the amount of this ware
    private m_pricePurchase: number //
    private m_priceBase: number //
    private m_priceRecommended: number // A recommended prize for the ware
    private m_priceFacory: number // Factory price
    private m_packageUnit: string // A word describing a package of this article
    private m_currency: string // The currency in choise for prizing of the ware/service
    private m_nobb: string // Nobb number
    private m_nobbOwner: string // Nobb owner number
    private m_nobbModule: string // Nobb module number
    private m_prizeType: string // Type of prize
    private m_itemType: string // A code describing the size
    private m_supplier: string // The name of the supplier
    private m_amount: number // To be used wherever this is relevant 
    private m_discountFactor: number // The given discount factor for this article
    private m_extData: ExtDataGW | null // Has a value if extended data was loaded for this article and an amount was set for the article
    private m_priceBook: number // The ID for the pricebook where to find this article
    private m_cathegoricWords:string | null // A collection of words for cathegoric search optimisations
}




// WEBPACK FOOTER //
// ./client/view/ArticleGW.tsx